import React, { useState, useRef, useEffect } from 'react';
import api from "../../api";

function bytesToSize(bytes) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (!bytes) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i];
}

export default function AdminMedia({ onUploaded }) {
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);

  const [dragActive, setDragActive] = useState(false);

  // Playlist selection
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState("");

  // Fetch playlists
  useEffect(() => {
    async function fetchPlaylists() {
      try {
        const res = await api.get("/api/playlist/list");
        setPlaylists(res.data);
      } catch (err) {
        console.error("Error fetching playlists:", err);
      }
    }
    fetchPlaylists();
  }, []);

  // Cleanup preview URLs
  useEffect(() => {
    return () => files.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
  }, [files]);

  const handleFiles = (selectedFiles) => {
    const newFiles = Array.from(selectedFiles).map(f => ({
      file: f,
      preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      title: "",
      artist: "",
      genre: "",
      progress: 0,
      status: "idle",
      size: f.size
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const uploadFile = async (fileObj, index) => {
    if (!selectedPlaylist) return alert("Please select a playlist first!");

    const formData = new FormData();
    formData.append("media", fileObj.file);
    formData.append("title", fileObj.title);
    formData.append("artist", fileObj.artist);
    formData.append("genre", fileObj.genre);

    try {
      setFiles(prev =>
        prev.map((p, i) => (i === index ? { ...p, status: "uploading" } : p))
      );

      const res = await api.post("/api/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded * 100) / e.total);
          setFiles(prev =>
            prev.map((p, i) => (i === index ? { ...p, progress: percent } : p))
          );
        }
      });

      const uploadedSong = res.data;

      // Step 2: Add to playlist
      await api.put(`/api/playlist/${selectedPlaylist}/add`, {
        songId: uploadedSong._id
      });

      setFiles(prev =>
        prev.map((p, i) =>
          i === index ? { ...p, status: "done", progress: 100 } : p
        )
      );

      if (onUploaded) onUploaded(uploadedSong);

    } catch (err) {
      console.error(err);
      setFiles(prev =>
        prev.map((p, i) => (i === index ? { ...p, status: "error" } : p))
      );
    }
  };

  const uploadAll = () => {
    if (!selectedPlaylist) return alert("Select a playlist first!");
    files.forEach((f, idx) => {
      if (f.status === "idle" || f.status === "error") uploadFile(f, idx);
    });
  };

  const updateField = (i, key, value) => {
    setFiles(prev => prev.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)));
  };

  const removeFile = (index) => {
    setFiles(prev => {
      const copy = [...prev];
      const f = copy.splice(index, 1)[0];
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return copy;
    });
  };

  return (
    <div className="max-w-5xl mx-auto p-4">

      {/* PLAYLIST SELECTOR */}
      <div className="bg-white p-5 rounded-xl shadow border mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Select Playlist</h3>

        <select
          value={selectedPlaylist}
          onChange={(e) => setSelectedPlaylist(e.target.value)}
          className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Choose Playlist</option>
          {playlists.map(pl => (
            <option key={pl._id} value={pl._id}>
              {pl.name}
            </option>
          ))}
        </select>
      </div>

      {/* DROPZONE */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        className={`border-2 border-dashed rounded-xl p-10 text-center transition 
          bg-white shadow-sm cursor-pointer
          ${dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"}
        `}
        onClick={() => inputRef.current.click()}
      >
        <p className="text-gray-700 text-lg">Drag & drop audio files here</p>
        <p className="text-gray-500">or click to browse</p>

        <input
          ref={inputRef}
          type="file"
          accept="audio/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {/* FILE LIST */}
      <div className="mt-6 space-y-5">
        {files.map((f, idx) => (
          <div key={idx} className="bg-white p-5 rounded-xl shadow border flex gap-4 items-start">

            {/* FILE META */}
            <div className="flex-1 space-y-3">

              <div className="flex gap-3">
                <input
                  className="border p-2 rounded flex-1"
                  placeholder="Song Title"
                  value={f.title}
                  onChange={(e) => updateField(idx, "title", e.target.value)}
                />

                <input
                  className="border p-2 rounded w-40"
                  placeholder="Artist"
                  value={f.artist}
                  onChange={(e) => updateField(idx, "artist", e.target.value)}
                />
              </div>

              {/* STATUS */}
              <div className="flex items-center gap-2 text-sm">
                {f.status === "uploading" && (
                  <span className="px-2 py-1 text-blue-700 bg-blue-100 rounded">
                    Uploading...
                  </span>
                )}
                {f.status === "done" && (
                  <span className="px-2 py-1 text-green-700 bg-green-100 rounded">
                    Done
                  </span>
                )}
                {f.status === "error" && (
                  <span className="px-2 py-1 text-red-700 bg-red-100 rounded">
                    Error
                  </span>
                )}
              </div>

              {/* PROGRESS BAR */}
              <div>
                <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
                  <div
                    style={{ width: `${f.progress}%` }}
                    className="h-full bg-blue-600 transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {f.progress}% • {bytesToSize(f.size)}
                </p>
              </div>

            </div>

            {/* ACTION BUTTONS */}
            <div className="flex flex-col gap-2">
              <button
                onClick={() => uploadFile(f, idx)}
                className="px-3 py-1 bg-blue-600 text-white rounded shadow text-sm hover:bg-blue-700"
              >
                Upload
              </button>

              <button
                onClick={() => removeFile(idx)}
                className="px-3 py-1 border border-gray-400 text-gray-700 rounded text-sm hover:bg-gray-100"
              >
                Remove
              </button>
            </div>

          </div>
        ))}
      </div>

      {files.length > 0 && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={uploadAll}
            className="px-6 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700"
          >
            Upload All
          </button>
        </div>
      )}

    </div>
  );
}
